import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import MapContainer from "../mapContainer";
import AddressPanel from "./addressPanel";
import CustomInput from "../inputFields/CustomInput";
import "animate.css/animate.css";
import httpRequest from "../../../HttpService";
import { useUserContext } from "../../../lib/user_context";
import AccordionNextButton from "../AccordionNextButton";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const sxToggleButton = {
  '@media (min-width: 1536px)': { padding: '8px 16px', fontSize: '1rem' },
};
const sxPanelWrapperBase = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '4px',
  padding: 0.5,
  transition: 'border-color 0.2s ease-in-out, border-width 0.2s ease-in-out',
};
const sxPanelWrapperFocused = {
  borderColor: 'primary.main',
  borderWidth: '2px',
};

const PICKUP_DELIVERY_PANEL_INDEX = 1;

const PickUpandDeliveryLocations = React.memo((props) => {
  const pickUpLocation = props.info[1]?.pickUpLocation || {};
  const deliveryLocation = props.info[1]?.deliveryLocation || {};

  const [mapType, setMapType] = useState("directions");
  const [activeInputFocus, setActiveInputFocus] = useState("consignee"); 
  const [shortestDistance, setShortestDistance] = useState(null);

  const shipperInputRef = useRef(null);
  const consigneeInputRef = useRef(null);
  const urlInputRef = useRef(null);

  const { sessionToken, sessionAccountAddress } = useUserContext();

useEffect(() => {
    console.log(`
🏠🏠🏠 ADDRESS ALERTS CHECK 🏠🏠🏠
Quote ID: ${props.info[0]?.quoteId}
Pickup Location ID: ${props.info[1]?.pickUpLocation?.addressID}
Delivery Location ID: ${props.info[1]?.deliveryLocation?.addressID}`);

    if (props.info && props.info[0]) {
        // Only set the 'required' alert if the location is NOT already populated from the quote
        if (!props.info[1]?.pickUpLocation?.addressID) {
            console.log(`⚠️ Setting pickUpLocation alert - no addressID found`);
            props.onAlert({"target":"pickUpLocation", "value":true});
        } else {
            console.log(`✅ Pickup location found - clearing alert`);
            props.onAlert({"target":"pickUpLocation", "value":false});
        }
        
        if (!props.info[1]?.deliveryLocation?.addressID) {
            console.log(`⚠️ Setting deliveryLocation alert - no addressID found`);
            props.onAlert({"target":"deliveryLocation", "value":true});
        } else {
            console.log(`✅ Delivery location found - clearing alert`);
            props.onAlert({"target":"deliveryLocation", "value":false});
        }
        
        console.log(`✅ Clearing Initial alert`);
        props.onAlert({"target":"Initial", "value":false});
    }
}, [props.info[0]?.quoteId, props.info[1]?.pickUpLocation, props.info[1]?.deliveryLocation]);

useEffect(() => {
  console.log("📍 AddressPanel - info[0]:", props.info[0]);
  console.log("📍 AddressPanel - accountNumber from info:", props.info[0]?.accountId);
  console.log("📍 AddressPanel - quoteId from info:", props.info[0]?.quoteId);
}, [props.info[0]]);

  useEffect(() => {
    if (props.expanded === PICKUP_DELIVERY_PANEL_INDEX) {
      setActiveInputFocus("consignee");
      const focusTimer = setTimeout(() => {
        if (consigneeInputRef.current) {
          if (typeof consigneeInputRef.current.focus === 'function') {
            consigneeInputRef.current.focus();
          } else {
            const actualInput = consigneeInputRef.current.querySelector('input');
            if (actualInput && typeof actualInput.focus === 'function') {
              actualInput.focus();
            } else {
              console.warn("Consignee input ref or its nested input could not be focused on panel expand.");
            }
          }
        }
      }, 100);
      return () => clearTimeout(focusTimer);
    }
  }, [props.expanded]);


  const handleRoutesLoaded = (shortestRoute) => {
    setShortestDistance(shortestRoute);
  };

  useEffect(() => {
    if (props.expanded !== PICKUP_DELIVERY_PANEL_INDEX) {
        return;
    }
    const timerId = setTimeout(() => {
        let targetRef = null;
        if (activeInputFocus === "shipper") {
            targetRef = shipperInputRef;
        } else if (activeInputFocus === "consignee") {
            targetRef = consigneeInputRef;
        }
        if (targetRef && targetRef.current) {
            if (typeof targetRef.current.focus === 'function') {
                targetRef.current.focus();
            } else {
                const actualInput = targetRef.current.querySelector('input');
                if (actualInput && typeof actualInput.focus === 'function') {
                    actualInput.focus();
                } else {
                    console.warn(`Input ref for '${activeInputFocus}' or its nested input does not have a focus method.`);
                }
            }
        }
    }, 0);
    return () => clearTimeout(timerId);
  }, [activeInputFocus, props.expanded]);

  const handleAddressPanelTab = (event, panelTypeComingFrom, addressWasSelectedByTab) => {
    if(event) event.preventDefault();
    if (panelTypeComingFrom === "shipper") {
      setActiveInputFocus("consignee");
    } else if (panelTypeComingFrom === "consignee") {
      setActiveInputFocus("shipper");
    }
  };

  const handleUrlInputKeyDown = (event) => {
    if (event.key === 'Tab') {
      // Standard browser tabbing will apply
    }
  };

  const changeMapType = (event, newMapType) => {
    if (newMapType !== null) {
        setMapType(newMapType);
    }
  };

  const handleAddressChange = (address, type, disableDialog = false) => {
    if (type === "consignee") {
      props.onChange({"target":"deliveryLocation", "value":address});
      props.onAlert({"target":"deliveryLocation", "value":false});
    } else { // shipper
      if (!disableDialog && pickUpLocation && pickUpLocation.addressID !== undefined && address.addressID !== pickUpLocation.addressID) {
        props.openDialog("openChangePickupDialog");
      }
      props.onChange({"target":"pickUpLocation", "value":address});
      props.onAlert({"target":"pickUpLocation", "value":false});
    }
    props.setCompletedStep(PICKUP_DELIVERY_PANEL_INDEX);
  };

  const handleContactDetailsChange = (event) => {
    // Check for the special 'clear' action sent from the child component
    if (event.type === 'clear') {
        const prefix = event.target.name; // 'sh' or 'co'
        const targetLocationKey = prefix === 'sh' ? 'pickUpLocation' : 'deliveryLocation';
        const currentLocationObject = props.info[1][targetLocationKey] || {};

        // Create a new object with all contact fields explicitly cleared
        const updatedLocationObject = {
            ...currentLocationObject,
            contactName: '',
            contactPhone: '',
            contactID: null
        };

        // Update the main state with the fully cleared object in one go
        props.onChange({ "target": targetLocationKey, "value": updatedLocationObject });
        return;
    }

    // This handles normal, single-field updates as the user types
    const { name, value } = event.target;
    const targetLocationKey = name.startsWith('sh') ? 'pickUpLocation' : 'deliveryLocation';

    let propertyName = '';
    if (name.includes('ContactName')) propertyName = 'contactName';
    if (name.includes('ContactPhone')) propertyName = 'contactPhone';
    if (name.includes('ContactID')) propertyName = 'contactID';
    
    const currentLocationObject = props.info[1][targetLocationKey] || {};
    const updatedLocationObject = {
        ...currentLocationObject,
        [propertyName]: value
    };

    props.onChange({ "target": targetLocationKey, "value": updatedLocationObject });
  };

  const handleFedexTrackNumChange = (event) => {
    props.onChange({"target":"URL", "value":event.target.value});
  };

  const handleNextPanel = async () => {
    if (!pickUpLocation.addressID || !deliveryLocation.addressID) {
        console.error("Next button: Pickup or Delivery location is not fully selected.");
        return;
    }
    if (pickUpLocation.addressID === deliveryLocation.addressID) {
        console.error("Next button: Pickup and Delivery locations cannot be the same.");
        return;
    }
    const drivDist = shortestDistance && shortestDistance.distance ? shortestDistance.distance : null;
    const shipperContactID = pickUpLocation.contactID || pickUpLocation.ContactID || null;
    const consigneeContactID = deliveryLocation.contactID || deliveryLocation.ContactID || null;
    if (!props.info || !props.info[0] || !props.info[0].quoteId) {
        console.error("Missing quoteId for posting addresses.");
        return;
    }
    try {
      const body = await httpRequest.postQuoteAddresses(props.info[0].quoteId,
        parseInt(pickUpLocation.addressID),
        shipperContactID,
        parseInt(deliveryLocation.addressID),
        consigneeContactID,
        drivDist);
      if (body && body.data) {
        props.onChange({"target":"quoteAddresses_data", "value":body.data});
        props.onNextPanel();
      } else {
        console.error("postQuoteAddresses response did not contain data:", body);
      }
    } catch (error) {
        console.error("Error in postQuoteAddresses:", error);
    }
  };
  
  const consignCoord = useMemo(() => ({
    lat: deliveryLocation.lat,
    long: deliveryLocation.long,
  }), [deliveryLocation.lat, deliveryLocation.long]);

  const shipperCoord = useMemo(() => ({
    lat: pickUpLocation.lat,
    long: pickUpLocation.long,
  }), [pickUpLocation.lat, pickUpLocation.long]);

  const currentAccountNumber = props.info && props.info[0] ? props.info[0].accountId : null;
  const currentQuoteId = props.info && props.info[0] ? props.info[0].quoteId : null;
  const currentCaller = props.info && props.info[0] ? props.info[0].caller : null;

  const isNextDisabled = !currentAccountNumber ||
                         !currentCaller ||
                         !pickUpLocation.addressID ||
                         !deliveryLocation.addressID ||
                         (pickUpLocation.addressID === deliveryLocation.addressID);

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2} style={{ flex: 1, minHeight: 0 }}>
        {/* Shipper Address Panel */}
        <Grid item xs={12} md={4} style={{ height: '100%' }}>
          <Box sx={activeInputFocus === "shipper" ? { ...sxPanelWrapperBase, ...sxPanelWrapperFocused } : sxPanelWrapperBase}>
            <AddressPanel
              type="shipper"
              onChange={handleAddressChange}
              initialSelected={pickUpLocation.addressID ? 0 : ""}
              selectedAddress={pickUpLocation}
              onChangeContact={handleContactDetailsChange}
              accountNumber={currentAccountNumber}
              quoteId={currentQuoteId}
              style={{ flex: 1, overflowY: 'auto', height: '100%' }}
              inputRef={shipperInputRef}
              onTabKey={handleAddressPanelTab}
              isActive={activeInputFocus === "shipper"}
              onFocus={() => setActiveInputFocus("shipper")}
            />
          </Box>
        </Grid>
        {/* Consignee Address Panel */}
        <Grid item xs={12} md={4} style={{ height: '100%' }}>
          <Box sx={activeInputFocus === "consignee" ? { ...sxPanelWrapperBase, ...sxPanelWrapperFocused } : sxPanelWrapperBase}>
            <AddressPanel
              type="consignee"
              onChange={handleAddressChange}
              initialSelected={deliveryLocation.addressID ? 0 : ""}
              selectedAddress={deliveryLocation}
              pickUpLocation={pickUpLocation}
              onChangeContact={handleContactDetailsChange}
              accountNumber={currentAccountNumber}
              quoteId={currentQuoteId}
              style={{ flex: 1, overflowY: 'auto', height: '100%' }}
              inputRef={consigneeInputRef}
              onTabKey={handleAddressPanelTab}
              isActive={activeInputFocus === "consignee"}
              onFocus={() => setActiveInputFocus("consignee")}
            />
          </Box>
        </Grid>
        {/* Map Display Area */}
        <Grid item xs={12} md={4} style={{ height: '100%' }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ToggleButtonGroup
              size="small"
              value={mapType}
              exclusive
              onChange={changeMapType}
              aria-label="map view toggle"
              style={{ marginBottom: '8px' }}
            >
              <ToggleButton value="directions" aria-label="directions map" sx={sxToggleButton}>
                Directions
              </ToggleButton>
              <ToggleButton value="place" aria-label="street view map" sx={sxToggleButton}>
                StreetView
              </ToggleButton>
            </ToggleButtonGroup>
            <div style={{ flex: 1, minHeight: 0, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
              <MapContainer
                accountNumber={currentAccountNumber}
                consignAddress={deliveryLocation.companyName}
                shipperAddress={pickUpLocation.companyName}
                consignCoord={consignCoord}
                shipperCoord={shipperCoord}
                type={mapType}
                style={{ height: '100%', width: '100%' }}
                onRoutesLoaded={handleRoutesLoaded}
              />
            </div>
          </div>
        </Grid>
      </Grid>
      {/* Bottom section for URL input and Next button */}
      <div style={{marginTop: '16px'}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <CustomInput
              type="text"
              icon="ClearIcon"
              inputType="text"
              label="Third Party URL"
              name="FedexTrackNum"
              iconTooltip="Clear URL"
              labelSize={4}
              onBlur={handleFedexTrackNumChange}
              inputRef={urlInputRef}
              onKeyDown={handleUrlInputKeyDown}
            />
          </Grid>
          <Grid item xs={12}>
            <AccordionNextButton
              onClick={handleNextPanel}
              disabled={isNextDisabled}
              panelIndex={PICKUP_DELIVERY_PANEL_INDEX}
              activePanel={props.expanded}
              errorMessage={
                !currentAccountNumber ? 'Please select an account.' :
                !currentCaller ? 'Please enter your name.' :
                !pickUpLocation.addressID ? 'Please select a pickup location.' :
                !deliveryLocation.addressID ? 'Please select a delivery location.' :
                (pickUpLocation.addressID === deliveryLocation.addressID ? 'Pickup and Delivery locations cannot be the same.' : '')
              }
            />
          </Grid>
        </Grid>
      </div>
    </div>
   );
});

export default PickUpandDeliveryLocations;